import { useEffect, useRef, useState } from 'react'
import { useT } from '@/lib/i18n'
import {
  ACKS, TAKE_INTRO, ENDINGS, ENDING_ORDER, PICK_LISTS, PHRASE_STATE, fillQuestion, factCard, pickFamily,
  type RingItem, type Picks, type PhraseState,
} from '@/components/onboarding/questionBank'
import { useDictation } from '@/components/onboarding/speech'
import { VoiceRingArc } from './VoiceRingArc'
import { useVoiceRing, useVoiceSheet } from './useVoiceRing'

/** The soft cap (bank v1 §6.6): a hint at 75 seconds, done at 90. */
const WRAP_AT = 75
const CAP_AT = 90

/**
 * The voice ring's sheet (Brian's voice-ring.js): asks what's left one at a
 * time. The core prompts onboarding didn't ask, one take per topic, then a few
 * quick picks. Spoken (dictated) or typed when there's no mic. Done for now
 * any time; nothing is lost. Skipped ones wait at the back of the line and come
 * back another day. A bottom sheet on a phone, a window on a computer.
 */
export function VoiceRingSheet() {
  const open = useVoiceSheet((s) => s.open)
  const setOpen = useVoiceSheet((s) => s.setOpen)
  const [ever, setEver] = useState(false)
  useEffect(() => { if (open) setEver(true) }, [open])
  useEffect(() => {
    if (!open) return
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [open, setOpen])
  const t = useT()
  return (
    <>
      <div className={`vr-scrim${open ? ' is-on' : ''}`} onClick={() => setOpen(false)} aria-hidden="true" />
      <div className={`vr-sheet${open ? ' is-on' : ''}`} role="dialog" aria-modal="true" aria-label={t('voice.sheetTitle')} aria-hidden={!open}>
        {ever && open && <SheetBody close={() => setOpen(false)} />}
      </div>
    </>
  )
}

function SheetBody({ close }: { close: () => void }) {
  const t = useT()
  const ring = useVoiceRing()
  const { progress, items, done } = ring
  const [skippedNow, setSkippedNow] = useState<Set<string>>(() => new Set())
  const [curId, setCurId] = useState<string | null>(null)
  const [firstTake, setFirstTake] = useState(true)
  const [typed, setTyped] = useState(false)
  const [draft, setDraft] = useState('')
  const [ack, setAck] = useState('')
  const [secs, setSecs] = useState<number | null>(null)
  const [heardNothing, setHeardNothing] = useState(false)
  const busy = useRef(false)
  const t0 = useRef(0)
  const tick = useRef<number | null>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)
  const draftRef = useRef('')
  draftRef.current = draft
  const dictation = useDictation((txt) => setDraft((d) => (d.trim() ? `${d.trim()} ${txt}` : txt)))
  const useType = typed || !dictation.available

  // The next one: not answered, and not skipped in this sitting.
  const pending = items.find((x) => !done(x) && !skippedNow.has(x.id)) ?? null
  const cur: RingItem | null = (curId ? items.find((x) => x.id === curId) : null) ?? null
  useEffect(() => {
    if (!ring.ready || busy.current) return
    if (!cur || done(cur) || skippedNow.has(cur.id)) setCurId(pending?.id ?? null)
  }, [ring.ready, pending?.id, cur?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (tick.current) window.clearInterval(tick.current); dictation.stop() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (useType && cur && cur.kind !== 'picks') window.setTimeout(() => textRef.current?.focus({ preventScroll: true }), 350) }, [useType, cur?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function stopClock() { if (tick.current) window.clearInterval(tick.current); tick.current = null; setSecs(null) }

  function next(after: () => void, wait: number) {
    busy.current = true
    window.setTimeout(() => {
      busy.current = false
      after()
      setAck(''); setDraft(''); setHeardNothing(false)
      setCurId(null)
    }, wait)
  }

  function submit(text: string) {
    if (!cur || !text.trim()) return
    if (cur.kind !== 'picks' && (cur.kind === 'topic' || cur.q.take)) setFirstTake(false)
    ring.answer(cur, text)
    setAck(ACKS[(progress.n + 1) % ACKS.length])
    next(() => {}, 900)
  }

  function skip() {
    if (!cur) return
    dictation.stop(); stopClock()
    ring.skip(cur)
    const id = cur.id
    next(() => setSkippedNow((s) => new Set(s).add(id)), 120)
  }

  function startRec() {
    setDraft(''); setHeardNothing(false)
    dictation.start()
    t0.current = performance.now()
    setSecs(0)
    tick.current = window.setInterval(() => {
      const s = (performance.now() - t0.current) / 1000
      setSecs(s)
      if (s >= CAP_AT) stopRec()
    }, 200)
  }
  function stopRec() {
    dictation.stop(); stopClock()
    // The last phrase lands a moment after the recognizer stops.
    window.setTimeout(() => {
      const d = draftRef.current
      if (d.trim()) submit(d)
      else setHeardNothing(true)
    }, 450)
  }

  const head = (
    <div className="vr-hd">
      <VoiceRingArc pct={progress.pct} />
      <span className="vr-hd__t">
        <b>{t('voice.sheetTitle')}</b>
        <small>{progress.full ? t('voice.complete') : t('voice.left', { left: progress.left, total: progress.total })}</small>
      </span>
      <button type="button" className="vr-later" onClick={close}>{t('voice.doneForNow')}</button>
    </div>
  )

  if (!ring.ready) return <>{head}<div className="vr-bd"><p className="vr-hint">{t('voice.loading')}</p></div></>

  if (!cur) {
    const full = progress.full
    return (
      <>
        {head}
        <div className="vr-bd">
          <div className="vr-full">
            <b>{full ? t('voice.wholeTitle') : t('voice.allForNowTitle')}</b>
            <span>{full ? t('voice.wholeBody') : t('voice.allForNowBody')}</span>
          </div>
          <div className="vr-row vr-row--center"><button type="button" className="vr-btn" onClick={close}>{t('voice.backToIt')}</button></div>
        </div>
      </>
    )
  }

  if (cur.kind === 'picks') {
    return <>{head}<PicksBody ring={ring} onDone={(p) => { ring.finishPicks(p); setAck(t('voice.ack0')); next(() => {}, 600) }} onSkip={skip} /></>
  }

  const isTake = cur.kind === 'topic' || cur.q.take
  const label = cur.kind === 'topic' ? t('voice.labelTopic', { topic: cur.q.label }) : isTake ? t('voice.labelTake') : t('voice.labelOwn')
  let text = fillQuestion(cur.kind === 'topic' ? cur.q.p : cur.q.t, ring.place)
  // "Last one." only when it is the last one left
  if (cur.id === 'c10' && progress.left > 1) text = text.replace(/^Last one\.\s*/, '')
  const listening = dictation.listening || secs != null

  return (
    <>
      {head}
      <div className="vr-bd">
        <div className="vr-lab">{label}</div>
        {isTake && firstTake && <p className="vr-hint vr-hint--intro">{TAKE_INTRO}</p>}
        <p className="vr-q">{text}</p>
        {cur.kind === 'core' && cur.q.card && <div className="vr-card">{factCard()}</div>}

        {useType ? (
          <>
            <textarea ref={textRef} className="vr-text" value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(draft) }}
              placeholder={t('voice.typePlaceholder')} aria-label={t('voice.yourAnswer')} />
            {!dictation.available && <p className="vr-hint vr-hint--left">{t('voice.noMic')}</p>}
          </>
        ) : (
          <div className="vr-mic">
            <button type="button" className={`vr-rec${listening ? ' is-on' : ''}`} onClick={() => (listening ? stopRec() : startRec())}
              aria-label={listening ? t('voice.stop') : t('voice.startAnswering')}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
            </button>
            <div className="vr-timer">{secs != null ? `${Math.floor(secs / 60)}:${String(Math.floor(secs % 60)).padStart(2, '0')}` : ''}</div>
            <div className="vr-hint">
              {heardNothing ? t('voice.heardNothing')
                : secs != null && secs > WRAP_AT ? t('voice.wrapUp')
                : listening ? t('voice.listening') : t('voice.tapToStart')}
            </div>
            {listening && draft && <p className="vr-heard">{draft}</p>}
          </div>
        )}

        <div className={`vr-ack${ack ? ' is-in' : ''}`} aria-live="polite">{ack}</div>
        <div className="vr-row">
          <span className="vr-links">
            <button type="button" className="vr-lnk" onClick={skip}>{t('voice.skipOne')}</button>
            {dictation.available && (
              <>
                <span aria-hidden="true"> · </span>
                <button type="button" className="vr-lnk" onClick={() => { dictation.stop(); stopClock(); setTyped(!typed) }}>
                  {useType ? t('voice.talkInstead') : t('voice.typeInstead')}
                </button>
              </>
            )}
          </span>
          {useType && <button type="button" className="vr-btn" disabled={!draft.trim()} onClick={() => submit(draft)}>{t('voice.next')}</button>}
        </div>
      </div>
    </>
  )
}

/** The quick picks (bank §6, short): the ending for the top topic's family,
 *  what they call the audience, the caption, swearing, and twelve phrases.
 *  Anything left alone stays out of the scripts. */
function PicksBody({ ring, onDone, onSkip }: {
  ring: ReturnType<typeof useVoiceRing>; onDone: (p: Picks) => void; onSkip: () => void
}) {
  const t = useT()
  const [p, setP] = useState<Picks>(() => ring.ring.picks ?? {})
  const { family, label } = pickFamily(ring.topicsByShare)
  const end = ENDINGS[family]
  function set(next: Picks) { setP(next); ring.setPicks(next) }
  const one = (k: 'audience' | 'caption' | 'swear', list: string[], stack = false) => (
    <div className={stack ? 'vr-stack' : 'vr-chips'}>
      {list.map((x, i) => (
        <button key={x} type="button" className={`vr-chip${p[k] === i ? ' is-on' : ''}`} aria-pressed={p[k] === i}
          onClick={() => set({ ...p, [k]: i })}>{x}</button>
      ))}
    </div>
  )
  const cycle: Record<string, PhraseState | undefined> = { undefined: 'a', a: 's', s: 'n', n: undefined }
  const ph = p.phrases ?? {}
  return (
    <div className="vr-bd">
      <div className="vr-lab">{t('voice.picksLabel')}</div>
      <p className="vr-q vr-q--sm">{t('voice.picksLede')}</p>

      <div className="vr-lab">{t('voice.picksEnding', { topic: label.toLowerCase() })}</div>
      <div className="vr-stack">
        {ENDING_ORDER.map((k, i) => (
          <button key={k} type="button" className={`vr-chip${p.ending === i ? ' is-on' : ''}`} aria-pressed={p.ending === i}
            onClick={() => set({ ...p, ending: i })}>“{end[k]}”</button>
        ))}
        <button type="button" className={`vr-chip${p.ending === 3 ? ' is-on' : ''}`} aria-pressed={p.ending === 3}
          onClick={() => set({ ...p, ending: 3 })}>{t('voice.picksNeither')}</button>
      </div>

      <div className="vr-lab">{t('voice.picksAudience')}</div>
      {one('audience', PICK_LISTS.audience)}
      <div className="vr-lab">{t('voice.picksCaption')}</div>
      {one('caption', PICK_LISTS.captions, true)}
      <div className="vr-lab">{t('voice.picksSwear')}</div>
      {one('swear', PICK_LISTS.swear)}

      <div className="vr-lab">{t('voice.picksPhrases')}</div>
      <div className="vr-chips vr-ph">
        {PICK_LISTS.phrases.map((x) => {
          const s = ph[x]
          return (
            <button key={x} type="button" className="vr-chip" data-s={s}
              onClick={() => {
                const nx = cycle[String(s)]
                const phrases = { ...ph }
                if (nx) phrases[x] = nx; else delete phrases[x]
                set({ ...p, phrases })
              }}>
              {x}{s && <small>{PHRASE_STATE[s]}</small>}
            </button>
          )
        })}
      </div>

      <div className="vr-row">
        <button type="button" className="vr-lnk" onClick={onSkip}>{t('voice.skipForNow')}</button>
        <button type="button" className="vr-btn" onClick={() => onDone(p)}>{t('voice.done')}</button>
      </div>
    </div>
  )
}
